import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Users, Plus, Trash2, Edit, Save, X, Loader2, 
    Eye, EyeOff, Check, Building2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des restaurants disponibles
const AVAILABLE_RESTAURANTS = [
    "Ma biche sur le toit",
    "Compagnie Française",
    "Coup de bambou",
    "Jacques Balma",
    "Jacques Blagnac",
    "Café Chouchou",
    "Sous les pins",
    "Compagnie Busca"
];

export default function AdminUsersPage() {
    const { isAdmin, token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Formulaire
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        restaurants: [],
        has_consolidated_access: false
    });

    // Charger les utilisateurs
    const loadUsers = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data.filter(u => u.email !== 'admin@allforyou.fr'));
            }
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Erreur lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            loadUsers();
        }
    }, [isAdmin]);

    // Rediriger si pas admin
    if (!isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            email: '',
            name: '',
            password: '',
            restaurants: [],
            has_consolidated_access: false
        });
        setEditingUser(null);
        setShowForm(false);
        setShowPassword(false);
    };

    // Ouvrir le formulaire pour édition
    const handleEdit = (user) => {
        setFormData({
            email: user.email,
            name: user.name || '',
            password: '', // Ne pas afficher le mot de passe existant
            restaurants: user.restaurants || [],
            has_consolidated_access: user.has_consolidated_access || false
        });
        setEditingUser(user);
        setShowForm(true);
    };

    // Toggle restaurant selection
    const toggleRestaurant = (restaurant) => {
        setFormData(prev => ({
            ...prev,
            restaurants: prev.restaurants.includes(restaurant)
                ? prev.restaurants.filter(r => r !== restaurant)
                : [...prev.restaurants, restaurant]
        }));
    };

    // Sélectionner/désélectionner tous les restaurants
    const toggleAllRestaurants = () => {
        setFormData(prev => ({
            ...prev,
            restaurants: prev.restaurants.length === AVAILABLE_RESTAURANTS.length
                ? []
                : [...AVAILABLE_RESTAURANTS]
        }));
    };

    // Sauvegarder l'utilisateur
    const handleSave = async () => {
        if (!formData.email || !formData.name) {
            toast.error('Email et nom sont obligatoires');
            return;
        }
        if (!editingUser && !formData.password) {
            toast.error('Le mot de passe est obligatoire pour un nouvel utilisateur');
            return;
        }
        if (formData.restaurants.length === 0) {
            toast.error('Sélectionnez au moins un restaurant');
            return;
        }

        setSaving(true);
        try {
            const url = editingUser 
                ? `${API_URL}/api/admin/users/${editingUser.email}`
                : `${API_URL}/api/admin/users`;
            
            const method = editingUser ? 'PUT' : 'POST';
            
            const body = {
                email: formData.email,
                name: formData.name,
                restaurants: formData.restaurants,
                has_consolidated_access: formData.has_consolidated_access
            };
            
            // Ajouter le mot de passe seulement s'il est défini
            if (formData.password) {
                body.password = formData.password;
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                toast.success(editingUser ? 'Utilisateur modifié' : 'Utilisateur créé');
                resetForm();
                loadUsers();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Erreur de connexion');
        } finally {
            setSaving(false);
        }
    };

    // Supprimer l'utilisateur
    const handleDelete = async (email) => {
        if (!confirm(`Supprimer l'utilisateur ${email} ?`)) return;

        try {
            const response = await fetch(`${API_URL}/api/admin/users/${email}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Utilisateur supprimé');
                loadUsers();
            } else {
                toast.error('Erreur lors de la suppression');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <Users className="w-8 h-8 text-primary" />
                            Gestion des utilisateurs
                        </h1>
                        <p className="text-slate-400 mt-1">
                            Créez et gérez les accès des utilisateurs
                        </p>
                    </div>
                    <Button
                        onClick={() => { resetForm(); setShowForm(true); }}
                        className="bg-primary hover:bg-primary/90"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel utilisateur
                    </Button>
                </div>

                {/* Formulaire de création/édition */}
                {showForm && (
                    <Card className="bg-slate-800/70 border-slate-700 mb-6">
                        <CardHeader>
                            <CardTitle className="text-white">
                                {editingUser ? `Modifier: ${editingUser.name}` : 'Nouvel utilisateur'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Email et Nom */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-slate-300">Email *</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        placeholder="email@exemple.com"
                                        disabled={!!editingUser}
                                        className="bg-slate-700 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Nom complet *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        placeholder="Prénom Nom"
                                        className="bg-slate-700 border-slate-600 text-white"
                                    />
                                </div>
                            </div>

                            {/* Mot de passe */}
                            <div>
                                <Label className="text-slate-300">
                                    Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
                                </Label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        placeholder="••••••••"
                                        className="bg-slate-700 border-slate-600 text-white pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Sélection des restaurants */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-slate-300">Restaurants accessibles *</Label>
                                    <button
                                        type="button"
                                        onClick={toggleAllRestaurants}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        {formData.restaurants.length === AVAILABLE_RESTAURANTS.length 
                                            ? 'Tout désélectionner' 
                                            : 'Tout sélectionner'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {AVAILABLE_RESTAURANTS.map(restaurant => (
                                        <button
                                            key={restaurant}
                                            type="button"
                                            onClick={() => toggleRestaurant(restaurant)}
                                            className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${
                                                formData.restaurants.includes(restaurant)
                                                    ? 'bg-primary/20 border-primary text-white'
                                                    : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded flex items-center justify-center ${
                                                formData.restaurants.includes(restaurant)
                                                    ? 'bg-primary'
                                                    : 'bg-slate-600'
                                            }`}>
                                                {formData.restaurants.includes(restaurant) && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <span className="text-sm">{restaurant}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Accès consolidé */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({...formData, has_consolidated_access: !formData.has_consolidated_access})}
                                    className={`flex items-center gap-3 p-4 rounded-lg border w-full transition-all ${
                                        formData.has_consolidated_access
                                            ? 'bg-emerald-500/20 border-emerald-500 text-white'
                                            : 'bg-slate-700/50 border-slate-600 text-slate-400'
                                    }`}
                                >
                                    <div className={`w-6 h-6 rounded flex items-center justify-center ${
                                        formData.has_consolidated_access ? 'bg-emerald-500' : 'bg-slate-600'
                                    }`}>
                                        {formData.has_consolidated_access && <Check className="w-4 h-4 text-white" />}
                                    </div>
                                    <div className="text-left">
                                        <p className="font-medium">Accès à la vue consolidée</p>
                                        <p className="text-xs opacity-70">
                                            Permet de voir les données agrégées de tous ses restaurants
                                        </p>
                                    </div>
                                </button>
                            </div>

                            {/* Boutons */}
                            <div className="flex gap-3 pt-4 border-t border-slate-700">
                                <Button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex-1 bg-primary hover:bg-primary/90"
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    {editingUser ? 'Modifier' : 'Créer'}
                                </Button>
                                <Button
                                    onClick={resetForm}
                                    variant="outline"
                                    className="border-slate-600 text-slate-300"
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Annuler
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Liste des utilisateurs */}
                <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white">Utilisateurs ({users.length})</CardTitle>
                        <CardDescription className="text-slate-400">
                            Liste des utilisateurs ayant accès à l'application
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            </div>
                        ) : users.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>Aucun utilisateur</p>
                                <p className="text-sm">Cliquez sur "Nouvel utilisateur" pour en créer un</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {users.map(user => (
                                    <div
                                        key={user.email}
                                        className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-slate-600"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-white">{user.name}</p>
                                                {user.has_consolidated_access && (
                                                    <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">
                                                        Consolidé
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-slate-400">{user.email}</p>
                                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                                                <Building2 className="w-3 h-3 text-slate-500" />
                                                {user.restaurants?.length > 0 ? (
                                                    user.restaurants.length === AVAILABLE_RESTAURANTS.length ? (
                                                        <span className="text-xs text-primary">Tous les restaurants</span>
                                                    ) : (
                                                        user.restaurants.map((r, i) => (
                                                            <span key={r} className="text-xs text-slate-500">
                                                                {r}{i < user.restaurants.length - 1 ? ', ' : ''}
                                                            </span>
                                                        ))
                                                    )
                                                ) : (
                                                    <span className="text-xs text-red-400">Aucun restaurant</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                onClick={() => handleEdit(user)}
                                                variant="ghost"
                                                size="sm"
                                                className="text-slate-400 hover:text-white"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                onClick={() => handleDelete(user.email)}
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
